import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type {
  BankStatementImportPreview,
  BankStatementImportRequest,
  BankStatementImportResult,
} from '../types';

type ApiEnvelope<T> = {
  Code?: number;
  data?: T;
};

const BANK_STATEMENT_IMPORT_API = '/umbraco/api/BankStatementImport';

const normalizeEnvelope = <T>(response: ApiEnvelope<T>): T => {
  const code = response.Code;
  if (code != null && code !== 1) {
    throw new Error('Request failed');
  }

  const payload = response.data;
  if (payload == null) {
    throw new Error('Empty response payload');
  }

  return payload;
};

export const bankStatementManagementImportApi = createApi({
  reducerPath: 'bankStatementManagementImportApi',
  baseQuery,
  endpoints: (builder) => ({
    parsePreview: builder.mutation<BankStatementImportPreview, { nodeKey: string; file: File }>({
      query: ({ nodeKey, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `${BANK_STATEMENT_IMPORT_API}/ParsePreview`,
          method: 'POST',
          params: { nodeKey },
          body: formData,
        };
      },
      transformResponse: (response: ApiEnvelope<BankStatementImportPreview>) =>
        normalizeEnvelope(response),
    }),
    importStatement: builder.mutation<
      BankStatementImportResult,
      { nodeKey: string; request: BankStatementImportRequest }
    >({
      query: ({ nodeKey, request }) => ({
        url: `${BANK_STATEMENT_IMPORT_API}/Import`,
        method: 'POST',
        params: { nodeKey },
        body: request,
      }),
      transformResponse: (response: ApiEnvelope<BankStatementImportResult>) =>
        normalizeEnvelope(response),
    }),
  }),
});

export const { useParsePreviewMutation, useImportStatementMutation } =
  bankStatementManagementImportApi;
